import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Button,
  PasswordInput,
  Toggle,
  AlertIcon,
  Separator,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { SettingsLock } from 'components';
import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { ESettingsAliases, SettingsContext } from 'settings';

import s from './RemoteControl.module.scss';

export const RemoteControl: FC = () => {
  const {
    state: {
      settings: {
        remoteControlPassword,
        remoteControlEnabled,
        removeControlPasswordUpdated,
      },
    },
    actions: { setSetting },
  } = useContext(SettingsContext);
  const { t } = useContext(LocalizationContext);

  const passwordRef = useRef<HTMLInputElement>(null);

  const [password, setPassword] = useState(remoteControlPassword);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const shouldNotifyPassword = useMemo(() => {
    const date = dayjs(removeControlPasswordUpdated);

    return date.isValid() && date.diff(dayjs(), 'months') > 1;
  }, [removeControlPasswordUpdated]);

  const isAvailable = useIsFeatureAvailable('remote_control');

  useEffect(() => {
    if (!remoteControlPassword) {
      setIsEditMode(true);
      Promise.resolve().then(() => passwordRef.current?.focus());
    }

    setPassword(remoteControlPassword);
  }, [remoteControlPassword]);

  return (
    <>
      <div className={s.settings}>
        <Toggle
          label={t('settings.shell.security.tightVNC.title')}
          description={t('setting.shell.remoteControl.description')}
          value={remoteControlEnabled}
          onChange={(value: boolean) =>
            setSetting({
              alias: ESettingsAliases.REMOTE_CONTROL_ENABLED,
              value: value ? '1' : '0',
            })
          }
          variant="secondary"
          emptyBg
          disabled={!isAvailable}
          rightContent={!isAvailable ? <SettingsLock /> : undefined}
          className={s.toggle}
        />
        <div className={s.helper}>
          <AlertIcon size="s" />
          {t('settings.shell.security.tightVNC.info')}
        </div>

        <Separator style={{ margin: '16px 0' }} />

        <div className={s.links}>
          {remoteControlEnabled && (
            <Button
              size="s"
              iconName="IncomeIcon"
              iconPosition="right"
              onClick={() => {
                open(
                  'https://dl.smartshell.gg/tvn/TVNCInstaller.exe',
                  '_blank'
                );
              }}
              variant="secondary"
            >
              {t('setting.shell.remoteControl.download')}
            </Button>
          )}
          <a
            href="https://tightvnc.com"
            target="_blank"
            rel="noreferrer"
            className={s.link}
          >
            TightVNC©
          </a>
        </div>
        <div className={s.field}>
          <div className={s.password}>
            <PasswordInput
              ref={passwordRef}
              variant="secondary"
              value={password}
              onChange={setPassword}
              disabled={!isEditMode || !remoteControlEnabled}
              error={
                !password && remoteControlEnabled
                  ? t('errors.required')
                  : password?.length > 8 && remoteControlEnabled
                  ? t('errors.maxLength', 8)
                  : password?.includes('@') && remoteControlEnabled
                  ? t('incorrect.value')
                  : undefined
              }
              autoComplete="off"
              bordered
            />
            <Button
              variant="secondary"
              onClick={() => {
                if (!isEditMode) {
                  return setIsEditMode(true);
                }
                if (
                  !password ||
                  password.length > 8 ||
                  password?.includes('@')
                ) {
                  return;
                }

                setIsEditMode(false);

                if (password === remoteControlPassword) return;

                setIsUpdating(true);

                return setSetting({
                  alias: ESettingsAliases.REMOTE_CONTROL_PASSWORD,
                  value: password,
                }).finally(() => setIsUpdating(false));
              }}
              disabled={isUpdating || !remoteControlEnabled}
              iconName={isEditMode ? 'CheckIcon' : 'EditIcon'}
              onlyIcon
              className={s.btn}
            />
          </div>

          <span className={s.password_text}>
            {t('settings.shell.security.tightVNC.subtitle')}
          </span>

          {shouldNotifyPassword && (
            <span className={s.message}>
              <AlertIcon className={s.icon} size="s" />
              {t('setting.shell.remoteControl.password.needChange')}
            </span>
          )}
        </div>
      </div>
    </>
  );
};
